/**
 * Canonical API Error Handling
 * Comprehensive error management with typed exceptions
 */

import type { ApiError } from "./types";

// ==================== Error Classes ====================

export class ApiException extends Error {
	public readonly code: string;
	public readonly status: number;
	public readonly details?: any;
	public readonly requestId?: string;
	public readonly timestamp: string;

	constructor(
		code: string,
		message: string,
		status: number = 500,
		details?: any,
		requestId?: string,
	) {
		super(message);
		this.name = "ApiException";
		this.code = code;
		this.status = status;
		this.details = details;
		this.requestId = requestId;
		this.timestamp = new Date().toISOString();
	}

	toJSON(): ApiError {
		return {
			code: this.code,
			message: this.message,
			details: this.details,
			requestId: this.requestId,
			timestamp: this.timestamp,
		};
	}
}

export class AuthenticationError extends ApiException {
	constructor(message: string = "Authentication required", requestId?: string) {
		super("AUTHENTICATION_REQUIRED", message, 401, undefined, requestId);
		this.name = "AuthenticationError";
	}
}

export class AuthorizationError extends ApiException {
	constructor(
		message: string = "Insufficient permissions",
		requestId?: string,
	) {
		super("AUTHORIZATION_FAILED", message, 403, undefined, requestId);
		this.name = "AuthorizationError";
	}
}

export class ValidationError extends ApiException {
	constructor(message: string, details?: any, requestId?: string) {
		super("VALIDATION_ERROR", message, 400, details, requestId);
		this.name = "ValidationError";
	}
}

export class NotFoundError extends ApiException {
	constructor(resource: string, requestId?: string) {
		super("NOT_FOUND", `${resource} not found`, 404, { resource }, requestId);
		this.name = "NotFoundError";
	}
}

export class ConflictError extends ApiException {
	constructor(message: string, details?: any, requestId?: string) {
		super("CONFLICT", message, 409, details, requestId);
		this.name = "ConflictError";
	}
}

export class RateLimitError extends ApiException {
	constructor(retryAfter?: number, requestId?: string) {
		super(
			"RATE_LIMIT_EXCEEDED",
			"Too many requests",
			429,
			{ retryAfter },
			requestId,
		);
		this.name = "RateLimitError";
	}
}

export class NetworkError extends ApiException {
	constructor(message: string = "Network error occurred", requestId?: string) {
		super("NETWORK_ERROR", message, 0, undefined, requestId);
		this.name = "NetworkError";
	}
}

export class ServiceUnavailableError extends ApiException {
	constructor(service: string, requestId?: string) {
		super(
			"SERVICE_UNAVAILABLE",
			`${service} service is unavailable`,
			503,
			{ service },
			requestId,
		);
		this.name = "ServiceUnavailableError";
	}
}

export class TimeoutError extends ApiException {
	constructor(timeout: number, requestId?: string) {
		super(
			"TIMEOUT",
			`Request timed out after ${timeout}ms`,
			408,
			{ timeout },
			requestId,
		);
		this.name = "TimeoutError";
	}
}

// ==================== Error Factory ====================

export function createApiError(
	response: Response,
	errorData?: any,
	requestId?: string,
): ApiException {
	const { status } = response;

	switch (status) {
		case 400:
			return new ValidationError(
				errorData?.message || "Invalid request data",
				errorData?.details,
				requestId,
			);

		case 401:
			return new AuthenticationError(
				errorData?.message || "Authentication required",
				requestId,
			);

		case 403:
			return new AuthorizationError(
				errorData?.message || "Insufficient permissions",
				requestId,
			);

		case 404:
			return new NotFoundError(errorData?.resource || "Resource", requestId);

		case 409:
			return new ConflictError(
				errorData?.message || "Request conflicts with current state",
				errorData?.details,
				requestId,
			);

		case 429:
			return new RateLimitError(errorData?.retryAfter, requestId);

		case 503:
			return new ServiceUnavailableError(
				errorData?.service || "API",
				requestId,
			);

		default:
			return new ApiException(
				errorData?.code || "API_ERROR",
				errorData?.message || `HTTP ${status}: ${response.statusText}`,
				status,
				errorData?.details,
				requestId,
			);
	}
}

// ==================== Error Utilities ====================

export function isRetryableError(error: ApiException): boolean {
	// Retry on network errors, timeouts, and server errors (5xx)
	return (
		error instanceof NetworkError ||
		error instanceof TimeoutError ||
		error instanceof ServiceUnavailableError ||
		error.status >= 500
	);
}

export function isAuthError(error: ApiException): boolean {
	return (
		error instanceof AuthenticationError || error instanceof AuthorizationError
	);
}

export function getRetryDelay(
	attempt: number,
	baseDelay: number = 1000,
): number {
	// Exponential backoff with jitter: baseDelay * 2^attempt + random(0, 1000)
	return Math.min(baseDelay * 2 ** attempt + Math.random() * 1000, 30000);
}

export function shouldRetry(
	error: ApiException,
	attempt: number,
	maxRetries: number,
): boolean {
	return attempt < maxRetries && isRetryableError(error);
}

// ==================== Error Logger ====================

export interface ErrorContext {
	method: string;
	url: string;
	headers?: Record<string, string>;
	body?: any;
	userId?: string;
	tenantId?: string;
}

export function logApiError(error: ApiException, context: ErrorContext): void {
	// Only log in development or when debug is enabled
	const isDevelopment = import.meta.env.DEV;
	const isDebugEnabled = import.meta.env.VITE_DEBUG_API_CALLS === "true";

	if (!isDevelopment && !isDebugEnabled) {
		return;
	}

	console.group(`🚨 API Error: ${error.code}`);
	console.error("Error:", error.message);
	console.error("Status:", error.status);
	console.error("Request ID:", error.requestId);
	console.error("Context:", {
		method: context.method,
		url: context.url,
		userId: context.userId,
		tenantId: context.tenantId,
	});

	if (error.details) {
		console.error("Details:", error.details);
	}

	if (isDevelopment) {
		console.error("Stack:", error.stack);
	}

	console.groupEnd();
}
